import { ActionIcon, Button, UnstyledButton } from '@mantine/core';
import {
  IconCalendarEvent,
  IconChevronLeft,
  IconChevronRight,
  IconClockExclamation,
  IconFileCertificate,
} from '@tabler/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { formatDay } from '../../format.js';
import { Panel } from '../../Panel.js';
import { CategoryBadge } from './CertificateParts.js';
import classes from './Compliance.module.css';
import { calendarEvents, monthGrid } from './certificate-view.js';
import { TableSkeleton, ViewHead } from './SurveySchedule.js';
import type { CertificateListItem } from './types.js';

const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;

/** Ay kaydırma: `YYYY-MM` + n ay. */
export function shiftMonth(month: string, delta: number): string {
  const [year, value] = month.split('-').map(Number) as [number, number];
  const index = year * 12 + (value - 1) + delta;
  return `${Math.floor(index / 12)}-${String((index % 12) + 1).padStart(2, '0')}`;
}

export interface CertificateCalendarProps {
  items: readonly CertificateListItem[] | null;
  today: string;
  onOpen: (id: string) => void;
  onBack: () => void;
}

/**
 * Certificate Calendar: ayın bitiş günleri ve sıradaki sörvey vadeleri (geçmişte kalan
 * sertifikalar hariç, K28); altta ayın listesi. Hafta pazartesiden başlar.
 */
export function CertificateCalendar({ items, today, onOpen, onBack }: CertificateCalendarProps) {
  const { t, i18n } = useTranslation();
  const k = (key: string, values: Record<string, unknown> = {}) =>
    t(`vesselCertificates.calendar.${key}`, values);
  const [month, setMonth] = useState(today.slice(0, 7));
  const events = items ? calendarEvents(items, month) : [];
  const weeks = monthGrid(month);
  const title = new Intl.DateTimeFormat(i18n.resolvedLanguage ?? 'en', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${month}-01T00:00:00Z`));

  return (
    <Panel>
      <ViewHead title={k('title')} icon={<IconCalendarEvent size={18} />} onBack={onBack} />
      <div className={classes.calendarHead}>
        <ActionIcon
          variant="default"
          aria-label={k('previous')}
          onClick={() => setMonth((m) => shiftMonth(m, -1))}
        >
          <IconChevronLeft size={16} />
        </ActionIcon>
        <span className={classes.calendarMonth} aria-live="polite">
          {title}
        </span>
        <ActionIcon
          variant="default"
          aria-label={k('next')}
          onClick={() => setMonth((m) => shiftMonth(m, 1))}
        >
          <IconChevronRight size={16} />
        </ActionIcon>
        <Button variant="subtle" size="xs" onClick={() => setMonth(today.slice(0, 7))}>
          {k('today')}
        </Button>
      </div>
      {items === null ? (
        <TableSkeleton />
      ) : (
        <>
          <table className={classes.calendarGrid} aria-label={k('grid')}>
            <thead>
              <tr>
                {WEEKDAYS.map((day) => (
                  <th key={day} scope="col" className={classes.calendarWeekday}>
                    {k(`weekdays.${day}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {weeks.map((week) => (
                <tr key={week.find(Boolean) ?? 'w'}>
                  {week.map((day, index) => {
                    const dayEvents = day ? events.filter((e) => e.day === day) : [];
                    return (
                      <td
                        key={day ?? `empty-${index}`}
                        className={classes.calendarDay}
                        data-empty={day ? undefined : true}
                        data-today={day === today || undefined}
                      >
                        {day ? (
                          <div className={classes.calendarCell}>
                            <span className={classes.calendarDate}>
                              <span className={classes.srOnly}>{formatDay(day)}</span>
                              <span aria-hidden>{Number(day.slice(8))}</span>
                            </span>
                            {dayEvents.slice(0, 3).map((event) => (
                              <UnstyledButton
                                key={`${event.kind}-${event.item.id}`}
                                className={classes.calendarEvent}
                                data-kind={event.kind}
                                title={event.item.name}
                                onClick={() => onOpen(event.item.id)}
                              >
                                {event.kind === 'expiry' ? (
                                  <IconFileCertificate size={12} aria-hidden />
                                ) : (
                                  <IconClockExclamation size={12} aria-hidden />
                                )}
                                <span>{event.item.name}</span>
                              </UnstyledButton>
                            ))}
                            {dayEvents.length > 3 ? (
                              <span className={classes.muted}>+{dayEvents.length - 3}</span>
                            ) : null}
                          </div>
                        ) : null}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          <div className={classes.calendarLegend}>
            <span>
              <IconFileCertificate size={14} aria-hidden /> {k('legend.expiry')}
            </span>
            <span>
              <IconClockExclamation size={14} aria-hidden /> {k('legend.survey')}
            </span>
          </div>
          <div className={classes.agenda}>
            <h3 className={classes.sectionTitle}>{k('agenda')}</h3>
            {events.length === 0 ? (
              <EmptyState title={k('empty')} />
            ) : (
              <ul className={classes.files}>
                {events.map((event) => (
                  <li key={`${event.kind}-${event.item.id}`} className={classes.agendaRow}>
                    <span className={classes.nowrap}>{formatDay(event.day)}</span>
                    <UnstyledButton
                      className={classes.fileName}
                      onClick={() => onOpen(event.item.id)}
                    >
                      {event.item.name}
                    </UnstyledButton>
                    <span className={classes.muted}>
                      {event.kind === 'expiry' ? k('legend.expiry') : k('legend.survey')}
                    </span>
                    <CategoryBadge type={event.item.type} />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </Panel>
  );
}
